import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UpdateMemberData, MemberData } from '../api_types';
import { logger } from '../shared/logger';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import MemberProfileSettings from './settings/MemberProfileSettings';
import { memberApi } from '../api';

const MemberSettings: React.FC<{ businessId: string, facilityId: string }> = ({ businessId, facilityId }) => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<string>('profile');
  const [userData, setUserData] = useState<MemberData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<UpdateMemberData>({});
  const [updateError, setUpdateError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setError('No user found');
        setLoading(false);
        return;
      }

      if (!facilityId) {
        setError('No facility ID found');
        setLoading(false);
        return;
      }

      try {
        const data = await memberApi.getMemberData(user.uid, businessId, facilityId);
        setUserData(data);
        setEditData({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch user data');
        logger.error('Error fetching user data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [user, facilityId]);

  const handleEdit = () => {
    setIsEditing(true);
    setUpdateError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setUpdateError(null);
    if (userData) {
      setEditData({
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone
      });
    }
  };

  const handleSave = async () => {
    if (!user || !userData || !facilityId) return;
    
    try {
      setLoading(true);
      const updatedData = await memberApi.updateMemberData(user.uid, editData, businessId, facilityId);
      setUserData(updatedData);
      setIsEditing(false);
      setUpdateError(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update user data');
      logger.error('Error updating user data:', error);
      setUpdateError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof UpdateMemberData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleNavClick = (section: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setActiveSection(section);
    // Update URL hash without scrolling
    window.history.pushState(null, '', `#${section}`);
  };

  if (!user) {
    return (
      <div className="p-6 bg-gray-50 text-center">
        <p>Please log in to access member settings.</p>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Member Settings</h1>
        <p className="text-gray-600 mt-2">Manage your account and required documents</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Sidebar */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-medium text-gray-900 mb-4">Settings Menu</h2>
            <nav className="space-y-2">
             <a 
                href="#profile" 
                onClick={handleNavClick('profile')}
                className={`block px-3 py-2 rounded-md ${
                  activeSection === 'profile' 
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Profile
              </a>
              
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="md:col-span-2">

          {activeSection === 'profile' && (
            <section id="profile">
              <MemberProfileSettings businessId={businessId} facilityId={facilityId} />
            </section>
          )}

        </div>
      </div>
    </div>
  );
};

export default MemberSettings; 